import api from "./api";
import { getStoredUser, getStoredClientId } from "../utils/tokenUtils";

/**
 * Project service aligned with backend routes
 */
export const projectService = {
  /**
   * List projects (POST /project with filters)
   */
  getProjects: async (body = {}) => {
    const user = getStoredUser();
    const storedClientId = getStoredClientId();

    // Determine client_id: use provided one, or fallback to stored/client_id, or user's client_id
    // For chief-admin, they can specify any client_id
    // For other users, use their assigned client_id
    const effectiveClientId =
      body.client_id || storedClientId || user?.client_id;
    const payload = {
      page: body.page !== undefined ? parseInt(body.page, 10) : 1,
      limit: body.limit !== undefined ? parseInt(body.limit, 10) : 10,
      is_active: body.is_active !== undefined ? body.is_active : true,
      is_deleted: body.is_deleted !== undefined ? body.is_deleted : false,
      sort_by: body.sort_by !== undefined ? body.sort_by : "created_at",
      sort_order: body.sort_order !== undefined ? body.sort_order : "desc",
      search_term: body.search_term !== undefined ? body.search_term : "",
      search_by: body.search_type !== undefined ? body.search_type : "",
      ...(body.filterParams || {}),
    };

    // Always include client_id if we have one
    if (effectiveClientId) {
      payload.client_id = effectiveClientId;
    }

    console.log("projectService.getProjects: Sending payload:", payload);
    console.log("projectService.getProjects: client_id source:", {
      provided: body.client_id,
      stored: storedClientId,
      user: user?.client_id,
      effective: effectiveClientId,
    });
    const response = await api.post("/project", payload);
    console.log("projectService.getProjects: Response:", response.data);
    return response.data;
  },

  /**
   * Create a new project (POST /project/create)
   */
  createProject: async (projectData) => {
    const response = await api.post("/project/create", projectData);
    return response.data;
  },

  /**
   * Create a project with sample suites, sections, and test cases (POST /project/create-sample)
   */
  createSampleProject: async (payload) => {
    const response = await api.post("/project/create-sample", payload);
    return response.data;
  },

  /**
   * Update a project (PATCH /project/update)
   */
  updateProject: async (projectData) => {
    const response = await api.patch("/project/update", projectData);
    return response.data;
  },

  /**
   * Soft delete a project (PATCH /project/delete)
   */
  deleteProject: async (project_id) => {
    const response = await api.patch("/project/delete", { project_id });
    return response.data;
  },

  /**
   * Deactivate a project (PATCH /project/deactivate)
   */
  deactivateProject: async (project_id) => {
    const response = await api.patch("/project/deactivate", { project_id });
    return response.data;
  },

  /**
   * Activate a project (PATCH /project/activate)
   */
  activateProject: async (project_id) => {
    const response = await api.patch("/project/activate", { project_id });
    return response.data;
  },

  /**
   * Convenience: list projects for a client (POST /project)
   */
  getUserProjects: async (client_id) => {
    return projectService.getProjects({ client_id });
  },

  /**
   * Get all users for a project (POST /project/users)
   */
  getProjectUsers: async (project_id, client_id = null) => {
    const payload = { project_id };
    if (client_id) {
      payload.client_id = client_id;
    }
    const response = await api.post("/project/users", payload);
    return response.data;
  },

  /**
   * Get assignable users for a project (all active client users for Assign To dropdown)
   * POST /project/users/assignable
   */
  getAssignableUsers: async (project_id, client_id = null) => {
    const payload = { project_id };
    if (client_id) {
      payload.client_id = client_id;
    }
    const response = await api.post("/project/users/assignable", payload);
    return response.data;
  },

  /**
   * Get a single project by project_id (POST /project/get)
   */
  getProject: async (project_id, client_id = null) => {
    const payload = { project_id };
    if (client_id) {
      payload.client_id = client_id;
    }
    const response = await api.post("/project/get", payload);
    return response.data;
  },

  /**
   * Get project types (POST /project/types)
   */
  getProjectTypes: async () => {
    const response = await api.post("/project/types");
    return response.data;
  },

  /**
   * Assign users to project with roles (POST /project/users/assign)
   */
  assignProjectUsers: async (project_id, client_id, members) => {
    const response = await api.post("/project/users/assign", {
      project_id,
      client_id,
      members,
    });
    return response.data;
  },

  /**
   * Complete a project (POST /project/complete)
   */
  completeProject: async (project_id, client_id) => {
    const response = await api.post("/project/complete", {
      project_id,
      client_id,
    });
    return response.data;
  },
};
